<script lang="ts">
    import { expansionSlugMap } from '@/data/expansion';
    import { wowthingData } from '@/shared/stores/data';
    import { UserCount } from '@/types';
    import { userState } from '@/user-home/state/user';
    import getPercentClass from '@/utils/get-percent-class';
    import type { StaticDataProfessionCategory } from '@/shared/stores/static/types';

    import { recipesState } from './state';

    import Ability from './Ability.svelte';
    import CollectibleCount from '@/components/collectible/CollectibleCount.svelte';
    import Options from './Options.svelte';

    type Props = {
        expansionSlug: string;
        professionSlug: string;
    };
    let { expansionSlug, professionSlug }: Props = $props();

    let [category, subCategories] = $derived.by(() => {
        const expansionId = expansionSlugMap[expansionSlug].id;
        const professionId = wowthingData.static.professionBySlug.get(professionSlug).id;
        const profession = wowthingData.static.professionById.get(professionId);

        const retCategory = profession.expansionCategory[expansionId].children[0];
        const retSubCategories: [StaticDataProfessionCategory, UserCount][] = [];
        for (const subCategory of retCategory.children) {
            const subStats =
                userState.recipes.stats[`${professionSlug}--${expansionSlug}--${subCategory.id}`];
            if (!subStats || subStats.total === 0) {
                continue;
            }

            let anyShown = false;
            for (const ability of subCategory.abilities) {
                const isIgnored = wowthingData.manual.ignoredSkillLineAbilitySpellIds.has(
                    ability.spellId
                );

                const abilityIds = [
                    ability.id,
                    ...(ability.extraRanks || []).map(([abilityId]) => abilityId),
                ];

                const abilityUserHas = userState.recipes.hasAbility[ability.id];

                abilityIds.forEach((_abilityId, index) => {
                    const userHas = abilityUserHas[index];
                    if (
                        (userHas && $recipesState.showCollected) ||
                        (!userHas && $recipesState.showUncollected && !isIgnored)
                    ) {
                        anyShown = true;
                        return;
                    }
                });
            }

            if (anyShown) {
                retSubCategories.push([subCategory, subStats]);
            }
        }

        return [retCategory, retSubCategories];
    });
</script>

<style lang="scss">
    .wrapper {
        columns: 4;
    }
    table {
        --image-margin-top: -6px;

        break-inside: avoid;
        margin-bottom: 1rem;
        overflow: hidden; /* Firefox fix */
        table-layout: auto;
        width: calc(20rem + 22px);
    }
    th {
        padding: 0.15rem 0.3rem;
    }
</style>

<Options {category} {expansionSlug} {professionSlug} />

<div class="wrapper">
    {#each subCategories.filter(([, subStats]) => subStats.total > 0) as [subCategory, subStats] (subCategory.id)}
        <table class="table table-striped" data-id={subCategory.id}>
            <thead>
                <tr>
                    <th
                        class={getPercentClass(subStats.percent)}
                        colspan="2"
                        data-tooltip={`Category ${subCategory.id}`}
                    >
                        <div class="flex-wrapper">
                            <span class="text-overflow">
                                {subCategory.name}
                            </span>
                            <CollectibleCount counts={subStats} />
                        </div>
                    </th>
                </tr>
            </thead>
            <tbody>
                {#each subCategory.abilities as ability (ability.id)}
                    {@const extraRanks = ability.extraRanks || []}
                    <Ability rank={1} {ability} />

                    {#each extraRanks, rankIndex}
                        <Ability rank={rankIndex + 2} {ability} />
                    {/each}
                {/each}
            </tbody>
        </table>
    {/each}
</div>
